/**
 * PATH: packages/ui/src/kanban/cardCompose.ts
 * FILE_ID: UI-KANBAN-CARD-COMPOSE-001
 * PURPOSE: Pure I→O helpers for the "convert a Kanban card into an email"
 *   action — turn the card's plain-text description into safe compose-body
 *   HTML, and validate / normalise a typed recipient address.
 * WHY:
 *   FEAT-2026-06-23-KANBAN-CARD-COMPOSE. The rail's "Send as email" action
 *   seeds the composer with the card's title (subject) + description (body).
 *   The composer body is HTML, but a card's `summary` is plain text from a
 *   Trello import / operator note — so it MUST be escaped (no stored HTML is
 *   ever trusted into the editor) and newline-formatted. Kept pure + separate
 *   so it's unit-testable without rendering the rail.
 * FLOW:
 *   ┌───────────────┐   ┌──────────────────┐   ┌──────────────────┐
 *   │ card.summary  │ → │ escape + nl→<br>  │ → │ compose body HTML│
 *   │ (plain text)  │   │  + paragraphs     │   │ (safe)           │
 *   └───────────────┘   └──────────────────┘   └──────────────────┘
 * RELATED:
 *   - ./components/KanbanCardComposePicker.tsx: recipient picker that fires the action.
 *   - ./components/KanbanBoardView.tsx: maps card → { to, subject, body }.
 */

/** Escape the five HTML-significant characters so card text can never inject
 *  markup into the compose editor. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Plain text → compose-body HTML. Blank-line-separated blocks become
 * `<p>`; single newlines become `<br>`. Empty / whitespace-only input
 * returns '' so the composer opens with a blank body (the signature seed
 * then fills it).
 */
export function plainTextToComposeHtml(text: string | null | undefined): string {
  const trimmed = (text ?? '').replace(/\r\n/g, '\n').trim()
  if (!trimmed) return ''
  return trimmed
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True when `value` is a syntactically valid email address (trimmed). */
export function isLikelyEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim())
}

/** Lower-cased, trimmed address for de-dupe + send. */
export function normaliseEmail(value: string): string {
  return value.trim().toLowerCase()
}
